import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface AdminTemplate {
  id: string;
  title: string;
  template_category: string | null;
  template_description: string | null;
  is_featured: boolean;
  template_order: number;
  created_at: string;
  creator_email: string;
  creator_name: string | null;
  fields_count: number;
}

export interface SearchFormResult {
  id: string;
  title: string;
  created_at: string;
  creator_name: string | null;
  fields_count: number;
}

export function useAdminTemplates({ category, search, page = 1 }: { category?: string | null; search?: string; page?: number }) {
  const [templates, setTemplates] = useState<AdminTemplate[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      const { data } = await supabase.rpc('admin_list_templates', {
        p_category: category || null,
        p_search: search || null,
        p_page: page,
        p_per_page: 25,
      });
      if (data) { setTemplates(data.templates ?? []); setTotal(data.total ?? 0); }
      setLoading(false);
    };
    fetchTemplates();
  }, [category, search, page, refreshKey]);

  return { templates, total, loading, refresh };
}

export async function adminPromoteToTemplate(formId: string, category?: string, description?: string, featured?: boolean) {
  const { error } = await supabase.rpc('admin_promote_to_template', {
    p_form_id: formId, p_category: category || null, p_description: description || null, p_featured: featured || false,
  });
  return { error: error?.message || null };
}

export async function adminDemoteTemplate(formId: string) {
  const { error } = await supabase.rpc('admin_demote_template', { p_form_id: formId });
  return { error: error?.message || null };
}

export async function adminUpdateTemplate(formId: string, updates: { category?: string; description?: string; featured?: boolean; order?: number }) {
  const { error } = await supabase.rpc('admin_update_template', {
    p_form_id: formId, p_category: updates.category ?? null, p_description: updates.description ?? null,
    p_featured: updates.featured ?? null, p_order: updates.order ?? null,
  });
  return { error: error?.message || null };
}

export async function adminSearchForms(search: string): Promise<SearchFormResult[]> {
  const { data } = await supabase.rpc('admin_search_forms', { p_search: search, p_limit: 10 });
  return data || [];
}
